"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-3" data-index={0}>
        <div className="h-5 w-16 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <ul className="flex items-center gap-7" data-index={0}>
      {menuItems.map((item, i) => <li key={item.id} data-index={i} className="relative group">
          <Link to={item.href || "#"} className="text-foreground font-default text-[15px] font-semibold tracking-[0.02em] relative inline-block py-1">
            <span className="relative z-10">{item.label}</span>
            <svg className="absolute left-0 -bottom-1 w-full h-2 overflow-visible pointer-events-none" viewBox="0 0 100 8" preserveAspectRatio="none" aria-hidden="true">
              <path d="M2 5 Q 15 1, 30 4 T 60 4 T 98 4" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" pathLength={100} strokeDasharray={100} strokeDashoffset={100} className="transition-[stroke-dashoffset] duration-300 ease-out group-hover:[stroke-dashoffset:0]" />
            </svg>
          </Link>
        </li>)}
    </ul>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 px-6" data-index={0}>
        <div className="h-10 w-40 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-10 w-32 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-10 w-36 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <ul className="flex flex-col px-6" data-index={0}>
      {menuItems.map((item, i) => <li key={item.id} data-index={i} className="py-3">
          <Link to={item.href || "#"} onClick={onNavigate} className="text-foreground font-default inline-block" style={{
        fontSize: "clamp(2rem, 8vw, 3rem)",
        lineHeight: 0.95,
        transform: "rotate(-1deg)"
      }}>
            {item.label}
          </Link>
          {i < menuItems.length - 1 && <svg className="w-full h-3 mt-2" viewBox="0 0 200 8" preserveAspectRatio="none" aria-hidden="true">
              <path d="M0 4 Q 25 0, 50 4 T 100 4 T 150 4 T 200 4" fill="none" stroke="var(--foreground)" strokeWidth="2" strokeLinecap="round" />
            </svg>}
        </li>)}
    </ul>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1123" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-300 ease-out bg-background text-foreground ${scrolled ? "bg-background/75 backdrop-blur-lg border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className="mx-auto max-w-[1440px] px-4 md:px-8">
        <div className="grid grid-cols-12 items-center gap-4 h-20">
          <div className="col-span-6 md:col-span-3 flex items-center gap-6">
            <Link to="/" className="inline-block" style={{
            transform: "rotate(-2deg)"
          }} aria-label="Niki B. Makeup home">
              <WvcLogo className="h-9 w-auto" />
            </Link>
            <Link to="/" className="hidden lg:block font-default uppercase text-foreground/70 text-[11px] tracking-[0.18em] hover:text-primary transition-colors">
              Pagina Principal
            </Link>
          </div>

          <div className="hidden md:flex col-span-6 flex-col items-center justify-center">
            <span className="font-default uppercase text-foreground/70 text-[11px] tracking-[0.18em] mb-1">
              § MENÚ
            </span>
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="col-span-6 md:col-span-3 flex items-center justify-end">
            <div className="hidden md:flex relative items-center">
              <svg className="absolute -left-12 -top-6 w-12 h-12 pointer-events-none" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <path d="M4 6 Q 14 14, 18 22 T 30 34" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" fill="none" />
                <path d="M30 34 L 24 30 M 30 34 L 32 27" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" fill="none" />
              </svg>
              <Button asChild className="bg-primary text-primary-foreground border border-primary rounded-lg font-default font-normal text-[15px] px-7 py-3 h-auto tracking-normal hover:bg-primary/90 transition-all duration-150 ease-out">
                <Link to="/BookingPage">RESERVAR AHORA</Link>
              </Button>
            </div>

            <div className="md:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button aria-label="Open menu" className="bg-secondary text-secondary-foreground border border-secondary rounded-lg h-11 w-11 p-0 hover:bg-secondary/90 transition-all duration-150">
                    <Menu className="size-5" strokeWidth={2.5} />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-background w-full sm:max-w-md border-l border-border p-0 pt-20">
                  <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                  <div className="px-6 mb-6">
                    <span className="font-default uppercase text-foreground/70 text-[11px] tracking-[0.18em]">
                      § MENÚ
                    </span>
                  </div>
                  <MenuProvider menu_id="16">
                    <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                  </MenuProvider>
                  <div className="px-6 mt-8">
                    <Button asChild onClick={() => setMobileOpen(false)} className="bg-primary text-primary-foreground border border-primary rounded-lg font-default font-normal text-base px-7 py-3 h-auto hover:bg-primary/90 transition-all">
                      <Link to="/BookingPage">RESERVAR AHORA</Link>
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}